const PADDING = 16;
const SCALE = 2;

export type ExportFormat = "png" | "svg";

/**
 * Clone an SVG element with padding and serialize it to an XML string.
 * Returns the serialized SVG data along with the padded dimensions.
 */
function cloneSvg(svg: SVGSVGElement): {
  svgData: string;
  width: number;
  height: number;
} {
  const bbox = svg.getBBox();

  const clone = svg.cloneNode(true) as SVGSVGElement;
  const viewBox = `${bbox.x - PADDING} ${bbox.y - PADDING} ${
    bbox.width + PADDING * 2
  } ${bbox.height + PADDING * 2}`;
  clone.setAttribute("viewBox", viewBox);
  clone.setAttribute("width", `${bbox.width + PADDING * 2}`);
  clone.setAttribute("height", `${bbox.height + PADDING * 2}`);
  clone.style.overflow = "visible";

  return {
    svgData: new XMLSerializer().serializeToString(clone),
    width: bbox.width + PADDING * 2,
    height: bbox.height + PADDING * 2,
  };
}

/**
 * Render serialized SVG data onto a scaled canvas and return the PNG data URL.
 */
function svgToPngDataUrl(
  svgData: string,
  width: number,
  height: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = width * SCALE;
    canvas.height = height * SCALE;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Failed to get canvas context"));
      return;
    }

    ctx.scale(SCALE, SCALE);

    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Failed to load SVG image"));
    const bytes = new TextEncoder().encode(svgData);
    const binarySvg = Array.from(bytes, (b) => String.fromCharCode(b)).join("");
    img.src = "data:image/svg+xml;base64," + btoa(binarySvg);
  });
}

export async function exportChart(
  container: HTMLElement,
  filename: string,
  format: ExportFormat
): Promise<void> {
  const svg = container.querySelector("svg");
  if (!svg) return;

  const { svgData, width, height } = cloneSvg(svg);

  if (format === "svg") {
    const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    downloadUrl(url, `${filename}.svg`);
    URL.revokeObjectURL(url);
    return;
  }

  const dataUrl = await svgToPngDataUrl(svgData, width, height);
  downloadUrl(dataUrl, `${filename}.png`);
}

export async function chartToDataUrl(container: Element): Promise<string> {
  const svg = container.querySelector("svg");
  if (!svg) {
    throw new Error("No SVG found in container");
  }

  const { svgData, width, height } = cloneSvg(svg);
  return svgToPngDataUrl(svgData, width, height);
}

function downloadUrl(url: string, filename: string): void {
  const link = document.createElement("a");
  link.download = filename;
  link.href = url;
  link.click();
}
